import React, { useState, useEffect } from "react";
import CustomTextField from "./CustomTextField";
import CustomDropdown from "./CustomDropdown";
import CustomButton from "./CustomButton";
import ReusableHeader from "./ReusableHeader";
import "../styles/layout.css";
import { toast } from "react-toastify";
import {useOrganizationApi} from "../api/organization";
import {useUsersApi} from "../api/users"
import { decodeJwt } from "../util/decodeJwt";
import { useParams, useNavigate, useLocation } from "react-router-dom";

export default function OrganizationCreateOrUpdateWidget() {
  const {getAllVendorUsers} = useUsersApi();
  const { createVendorOrg,updateVendorOrg,getVendorOrgById } = useOrganizationApi()
  const location = useLocation();
  const navigate = useNavigate();
  const returnPath = location.state?.returnPath || "/organizations";
  const { id } = useParams();
  const isEditMode = Boolean(id);

  const [formData, setFormData] = useState({
    name: "",
    type: "",
    address: "",
    phoneNumber: "",
    email: "",
    gst: "",
    status: "active",
    userId: "",
  });

  const [loading, setLoading] = useState(false);
  const [userOptions, setUserOptions] = useState([]);
  const [isUserDropdownDisabled, setIsUserDropdownDisabled] = useState(false);
  const [currentUser, setCurrentUser] = useState(null);

  const handleChange = (key, value) => {
    setFormData((prev) => ({ ...prev, [key]: value }));
  };

  // ✅ Get current user from token
  useEffect(() => {
    const token = localStorage.getItem("token");
    if (token) {
      const decoded = decodeJwt(token);
      setCurrentUser(decoded);
      setFormData((prev) => ({
        ...prev,
        userId: decoded?._id || "",
      }));
    }
  }, []);

  // ✅ Load users
  useEffect(() => {
    if (!currentUser) return;

    const loadUsers = async () => {
      try {
        if (currentUser.role === "admin") {
          const res = await getAllVendorUsers({ limit: 50, offset: 0 });
          if (res.success && res.data) {
            const options = res.data.map((u) => ({
              value: u._id,
              label: `${u.name} (${u.phoneNumber})`,
            }));
            setUserOptions(options);
          }
        } else {
          setUserOptions([
            {
              value: currentUser._id,
              label: `${currentUser.name} (${currentUser.phoneNumber})`,
            },
          ]);
          setIsUserDropdownDisabled(true);
        }
      } catch (err) {
        toast.error(err.message || "Failed to load users");
      }
    };

    loadUsers();
  }, [currentUser]);

  // ✅ Load existing org if editing
  useEffect(() => {
    if (isEditMode) {
      (async () => {
        try {
          const org = await getVendorOrgById(id);
          setFormData({
            name: org.name || "",
            type: org.type || "",
            address: org.address || "",
            phoneNumber: org.phoneNumber || "",
            email: org.email || "",
            gst: org.gst || "",
            status: org.status || "active",
            userId: org.userId || currentUser?._id || "",
          });
        } catch (err) {
          toast.error("Failed to load organization details");
        }
      })();
    }
  }, [id, currentUser]);

  // Auto-select organization type if only one option
  useEffect(() => {
    const allTypes = [
      { value: "manufacturer", label: "Manufacturer" },
      { value: "distributor", label: "Distributor" },
      { value: "dealer", label: "Dealer" },
      { value: "customer", label: "Customer" },
    ];

    let availableTypes = [];

    switch (currentUser?.role) {
      case "admin":
       availableTypes = allTypes.filter(t => ["manufacturer"].includes(t.value));
        break;
      case "manufacturer":
        availableTypes = allTypes.filter(t => ["distributor"].includes(t.value));
        break;
      case "distributor":
        availableTypes = allTypes.filter(t => ["dealer"].includes(t.value));
        break;
      case "dealer":
        availableTypes = allTypes.filter(t => ["customer"].includes(t.value));
        break;
      default:
        availableTypes = [];
    }

    if (availableTypes.length === 1 && !formData.type) {
      setFormData(prev => ({ ...prev, type: availableTypes[0].value }));
    }
  }, [currentUser]);

  // Auto-select user if only one option
  useEffect(() => {
    if (userOptions.length === 1 && !formData.userId) {
      setFormData(prev => ({ ...prev, userId: userOptions[0].value }));
    }
  }, [userOptions]);


  const handleSubmit = async () => {
    if (!formData.name || !formData.phoneNumber || !formData.email || !formData.userId) {
      toast.warn("⚠️ Please fill in all required fields including selecting a user");
      return;
    }

    try {
      setLoading(true);

      if (isEditMode) {
        await updateVendorOrg(id, formData);
        toast.success("Organization updated successfully!");
      } else {
        const newOrg = await createVendorOrg(formData);
        toast.success("Organization created successfully!");

        // ✅ After creation, navigate back to returnPath
        navigate(returnPath, { state: { newOrgId: newOrg.data._id } });
        return;
      }

      navigate(returnPath);
    } catch (error) {
      toast.error(error.message || "❌ Something went wrong!");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="org-container">
      <ReusableHeader title="Organization Management" />
      <div className="org-form-card">
        <div className="org-columns">
          <div className="org-column-left">
            <CustomTextField
              label="Organization Name"
              value={formData.name}
              onChange={(e) => handleChange("name", e.target.value)}
            />
            <CustomTextField
              label="Address"
              value={formData.address}
              onChange={(e) => handleChange("address", e.target.value)}
              multiline
              rows={5}
              minRows={5}
            />
            <CustomTextField
              label="Email ID"
              value={formData.email}
              onChange={(e) => handleChange("email", e.target.value)}
            />
          </div>

          <div className="org-column-right">
          <CustomDropdown
            label="Organization Type"
            value={formData.type}
            onChange={(val) => handleChange("type", val)}
            options={(() => {
              const allTypes = [
                { value: "manufacturer", label: "Manufacturer" },
                { value: "distributor", label: "Distributor" },
                { value: "dealer", label: "Dealer" },
                { value: "customer", label: "Customer" },
              ];

              switch (currentUser?.role) {
                case "admin":
                 return allTypes.filter(t => ["manufacturer"].includes(t.value));
                case "manufacturer":
                  return allTypes.filter(t => ["distributor"].includes(t.value));
                case "distributor":
                  return allTypes.filter(t => ["dealer"].includes(t.value));
                case "dealer":
                  return allTypes.filter(t => ["customer"].includes(t.value));
                default:
                  return []; // nothing for other roles
              }
            })()}
            isMultiSelect={false}
          />

            <CustomDropdown
              label="Status"
              value={formData.status}
              onChange={(val) => handleChange("status", val)}
              options={[
                { value: "active", label: "Active" },
                { value: "inactive", label: "Inactive" },
              ]}
              isMultiSelect={false}
            />
            <CustomTextField
              label="Phone Number"
              numericOnly={true}
              value={formData.phoneNumber}
              onChange={(e) => handleChange("phoneNumber", e.target.value)}
            />
            <CustomTextField
              label="GST Number"
              value={formData.gst}
              onChange={(e) => handleChange("gst", e.target.value)}
            />
            {/* <CustomDropdown
              label="Assign User"
              value={formData.userId}
              onChange={(val) => handleChange("userId", val)}
              options={userOptions}
              disabled={isUserDropdownDisabled}
              isMultiSelect={false}
            /> */}
          </div>
        </div>

        <div className="org-buttons">
          <CustomButton
            label="Cancel"
            className="org-cancel"
            onClick={() => navigate("/organizations")}
          />
          <CustomButton
            label={loading ? (isEditMode ? "Updating..." : "Creating...") : isEditMode ? "Update" : "Create"}
            className="org-create"
            onClick={handleSubmit}
            disabled={loading}
          />
        </div>
      </div>
    </div>
  );
}
